/**
 * Owned Media — hooks de tRPC. Los tipos salen del AppRouter (RouterOutputs),
 * nunca de `as any`. `useOmlAccess` es el probe del menú/ruta (nunca throwea).
 */
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import { trpc } from '@/lib/trpc';
import { toastError } from '@/lib/toast-error';
import type { RouterInputs, RouterOutputs } from '@/lib/api-types';

export type OmlAccessProbe = RouterOutputs['oml']['accounts']['checkAccess'];
export type OmlAccountsList = RouterOutputs['oml']['accounts']['list'];
export type OmlAccountCard = OmlAccountsList['accounts'][number];
export type OmlAccountDetail = RouterOutputs['oml']['accounts']['get'];
export type OmlConfig = RouterOutputs['oml']['admin']['config'];
export type OmlHealth = RouterOutputs['oml']['transversal']['health'];
export type OmlRequestsList = RouterOutputs['oml']['access']['list'];
export type OmlHistoryPage = RouterOutputs['oml']['history']['list'];
export type OmlAuditPage = RouterOutputs['oml']['history']['audit'];
export type OmlEntityCreateInput = RouterInputs['oml']['entities']['create'];
export type OmlEntityUpdateInput = RouterInputs['oml']['entities']['update'];

const ACCESS_OPTS = { retry: false, staleTime: 5 * 60_000 } as const;

export function useOmlAccess(enabled = true) {
  return trpc.oml.accounts.checkAccess.useQuery(undefined, { ...ACCESS_OPTS, enabled });
}

export function useOmlConfig() {
  return trpc.oml.admin.config.useQuery(undefined, { staleTime: 60_000 });
}

export function useOmlAccounts(includeArchived = false) {
  return trpc.oml.accounts.list.useQuery({ includeArchived }, { staleTime: 30_000 });
}

export function useOmlAccount(accountId: string | undefined) {
  return trpc.oml.accounts.get.useQuery({ accountId: accountId ?? '' }, { enabled: !!accountId, retry: false });
}

export function useOmlCommunity(enabled = true) {
  return trpc.oml.admin.listCommunityForPicker.useQuery(undefined, { enabled, staleTime: 5 * 60_000 });
}

/** Invalida lo que muestra una cuenta después de escribir sobre ella. */
export function useOmlInvalidate() {
  const utils = trpc.useUtils();
  return (accountId?: string) => {
    void utils.oml.accounts.list.invalidate();
    if (accountId) {
      void utils.oml.accounts.get.invalidate({ accountId });
      void utils.oml.history.list.invalidate({ accountId });
    }
    void utils.oml.transversal.invalidate();
  };
}

export function useOmlMutations() {
  const { t } = useTranslation();
  const invalidate = useOmlInvalidate();
  const onError = (err: unknown) => toastError(err, 'common.toast.error_al_guardar');
  const saved = () => toast.success(t('oml.toast.saved', 'Guardado'));

  const createAccount = trpc.oml.accounts.create.useMutation({ onSuccess: () => { invalidate(); saved(); }, onError });
  const updateAccount = trpc.oml.accounts.update.useMutation({ onSuccess: (r) => { invalidate(r.account.id); saved(); } });
  const archiveAccount = trpc.oml.accounts.archive.useMutation({ onSuccess: () => { invalidate(); toast.success(t('oml.toast.archived', 'Archivado')); }, onError });
  const restoreAccount = trpc.oml.accounts.restore.useMutation({ onSuccess: () => { invalidate(); toast.success(t('oml.toast.restored', 'Restaurado')); }, onError });
  const createEntity = trpc.oml.entities.create.useMutation({ onSuccess: (r) => { invalidate(r.item.accountId); saved(); } });
  const updateEntity = trpc.oml.entities.update.useMutation({ onSuccess: (r) => { invalidate(r.item.accountId); saved(); } });
  const archiveEntity = trpc.oml.entities.archive.useMutation({ onSuccess: (r) => { invalidate(r.item.accountId); saved(); }, onError });
  const duplicateEntity = trpc.oml.entities.duplicate.useMutation({ onSuccess: (r) => { invalidate(r.item.accountId); toast.success(t('oml.toast.duplicated', 'Duplicado')); }, onError });
  const deleteLink = trpc.oml.entities.deleteLink.useMutation({ onSuccess: () => { invalidate(); toast.success(t('oml.toast.deleted', 'Eliminado')); }, onError });
  const revokeAccess = trpc.oml.entities.revokeAccess.useMutation({ onSuccess: (r) => { invalidate(r.item.accountId); saved(); }, onError });
  const renewAccess = trpc.oml.entities.renewAccess.useMutation({ onSuccess: (r) => { invalidate(r.item.accountId); saved(); }, onError });
  const closeAssignment = trpc.oml.entities.closeAssignment.useMutation({ onSuccess: (r) => { invalidate(r.item.accountId); saved(); }, onError });
  const requestAccess = trpc.oml.access.request.useMutation({ onSuccess: (r) => toast.success(r.duplicate ? t('oml.toast.request_pending', 'Ya tenías una solicitud pendiente') : t('oml.toast.request_sent', 'Solicitud enviada al data owner')), onError });
  const logRestrictedView = trpc.oml.accounts.logRestrictedView.useMutation();
  const logDenied = trpc.oml.accounts.logDenied.useMutation();
  const exportAccounts = trpc.oml.export.accounts.useMutation({ onError });
  const exportEntities = trpc.oml.export.entities.useMutation({ onError });
  const accountPdf = trpc.oml.export.accountPdf.useMutation({ onError });
  const putDraft = trpc.oml.views.putDraft.useMutation();
  const deleteDraft = trpc.oml.views.deleteDraft.useMutation();
  const saveView = trpc.oml.views.saveView.useMutation({ onSuccess: () => toast.success(t('oml.toast.view_saved', 'Vista guardada')), onError });
  const deleteView = trpc.oml.views.deleteView.useMutation({ onError });
  const uploadUrl = trpc.oml.assets.uploadUrl.useMutation({ onError });

  return {
    createAccount, updateAccount, archiveAccount, restoreAccount, createEntity, updateEntity, archiveEntity, duplicateEntity, deleteLink, revokeAccess, renewAccess, closeAssignment,
    requestAccess, logRestrictedView, logDenied, exportAccounts, exportEntities, accountPdf, putDraft, deleteDraft, saveView, deleteView, uploadUrl,
  };
}

export function useOmlAdminMutations() {
  const { t } = useTranslation();
  const utils = trpc.useUtils();
  const onError = (err: unknown) => toastError(err, 'common.toast.error_al_guardar');
  const done = () => { void utils.oml.admin.invalidate(); void utils.oml.accounts.checkAccess.invalidate(); void utils.profile.getVisibility.invalidate(); toast.success(t('oml.toast.saved', 'Guardado')); };
  return {
    setGovernance: trpc.oml.admin.setGovernance.useMutation({ onSuccess: done, onError }),
    setCatalogs: trpc.oml.admin.setCatalogs.useMutation({ onSuccess: done, onError }),
    setSettings: trpc.oml.admin.setSettings.useMutation({ onSuccess: done, onError }),
    approve: trpc.oml.access.approve.useMutation({ onSuccess: () => { void utils.oml.access.invalidate(); toast.success(t('oml.toast.approved', 'Aprobada')); }, onError }),
    reject: trpc.oml.access.reject.useMutation({ onSuccess: () => { void utils.oml.access.invalidate(); toast.success(t('oml.toast.rejected', 'Rechazada')); }, onError }),
    revokeGrant: trpc.oml.access.revokeGrant.useMutation({ onSuccess: () => { void utils.oml.access.invalidate(); toast.success(t('oml.toast.revoked', 'Acceso revocado')); }, onError }),
    physicalDelete: trpc.oml.admin.physicalDelete.useMutation({ onSuccess: () => { void utils.oml.invalidate(); toast.success(t('oml.toast.deleted', 'Eliminado')); }, onError }),
  };
}
